// src/components/circles/CircleFocus.jsx
//
// Who's in the circle, what it's on, and who has got to it.
//
// This is the together half of a circle: everyone on the same module, and you
// can see who has finished it. Deliberately NOT a leaderboard - no score, no
// streak, no ordering by progress, no "how far", nobody "behind", and no
// presence dot saying who is online. It answers one question: is there anyone
// I could talk to about this yet.
//
// Anyone in the circle can pick or change the module. Nobody owns a circle.

import { useState } from 'react';
import { BookOpen, Check, ChevronDown, Circle as CircleIcon } from 'lucide-react';
import { friendshipModulesData } from '../../data/friendshipModulesMetadata';
import friendCirclesService from '../../services/circles/FriendCirclesService';

export default function CircleFocus({ circle, userId, memberNames = {}, onChanged }) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const currentModule = circle?.settings?.currentModule ?? null;
  const meta = currentModule ? friendshipModulesData.find((m) => m.id === currentModule) : null;
  const members = circle?.members || [];

  const hasRead = (uid) =>
    (circle?.memberStatus?.[uid]?.modulesRead || []).includes(currentModule);
  const nameFor = (uid) => (uid === userId ? 'You' : memberNames[uid] || 'A friend');

  const pick = async (moduleId) => {
    setBusy(true);
    setError('');
    const res = await friendCirclesService.setCurrentModule(circle.id, moduleId);
    setBusy(false);
    if (!res.success) {
      setError("That didn't save. Check your connection and try again.");
      return;
    }
    setPicking(false);
    onChanged?.();
  };

  return (
    <section
      aria-labelledby="circle-focus-title"
      className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/10"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center flex-shrink-0">
            <BookOpen aria-hidden="true" className="w-5 h-5 text-teal-300" />
          </div>
          <div className="min-w-0">
            <h2 id="circle-focus-title" className="text-lg font-bold text-white">What we&apos;re on</h2>
            <p className="text-sm text-slate-300 mt-1">
              {meta ? `Module ${meta.id}: ${meta.title}` : 'Nobody has picked a module for the circle yet.'}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setPicking((v) => !v)}
          aria-expanded={picking}
          aria-controls="circle-module-picker"
          className="flex items-center gap-1 text-sm text-purple-200 hover:text-white px-3 py-2 rounded-lg border border-purple-400/30 hover:border-purple-300/60 transition-colors"
        >
          {currentModule ? 'Change' : 'Pick one'}
          <ChevronDown aria-hidden="true" className={`w-4 h-4 transition-transform ${picking ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {picking && (
        <div id="circle-module-picker" className="mb-4 grid gap-2">
          {friendshipModulesData.map((m) => (
            <button
              type="button"
              key={m.id}
              disabled={busy}
              onClick={() => pick(m.id)}
              aria-pressed={m.id === currentModule}
              className={`text-left px-4 py-3 rounded-lg border transition-colors disabled:opacity-50 ${
                m.id === currentModule
                  ? 'bg-teal-500/15 border-teal-400/40 text-white'
                  : 'bg-slate-900/40 border-white/10 text-slate-200 hover:bg-slate-900/70'
              }`}
            >
              <span className="text-sm font-medium">{m.id}. {m.title}</span>
            </button>
          ))}
          <p className="text-xs text-slate-400">Anyone in the circle can change this.</p>
        </div>
      )}

      <ul className="space-y-2" aria-label="Who's in the circle">
        {members.map((uid) => {
          const done = currentModule != null && hasRead(uid);
          return (
            <li key={uid} className="flex items-center gap-3 px-4 py-2.5 bg-slate-900/40 rounded-lg">
              {currentModule != null && (done
                ? <Check aria-hidden="true" className="w-4 h-4 text-teal-300 flex-shrink-0" />
                : <CircleIcon aria-hidden="true" className="w-4 h-4 text-slate-500 flex-shrink-0" />)}
              <span className={`text-sm min-w-0 break-words ${done ? 'text-white' : 'text-slate-300'}`}>
                {nameFor(uid)}
              </span>
              {done && <span className="text-xs text-teal-200 ml-auto flex-shrink-0">finished it</span>}
            </li>
          );
        })}
      </ul>

      {currentModule != null && (
        <p className="text-xs text-slate-400 pt-3">
          Whether someone has finished it, and nothing else. What anyone writes in a module stays theirs.
        </p>
      )}

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-200 bg-red-500/10 border border-red-400/30 rounded-lg px-3 py-2">
          {error}
        </p>
      )}
    </section>
  );
}
